import { ref } from 'vue'
import { createPaymentLink, type CheckoutMode } from '@/services/subscription'
import { ApiError } from '@/services/http'

/**
 * Shareable payment link flow: from the recap step the subscriber hands off the
 * already-created contract draft id + mode (`prepare`), navigates to the
 * dedicated page, and that page generates the link on mount (`generate`). The
 * draft itself is created on the recap (see `useSubscriptionFinalize`), so this
 * flow only ever creates the Stripe Payment Link.
 *
 * State is a module-level singleton so the recap component and the off-journey
 * page share it. A page loaded without a prior `prepare` (hard reload / direct
 * visit) therefore has no context and should redirect home.
 */
type Pending = { draftId: string; mode: CheckoutMode }

/** Draft id + mode staged by the recap step, awaiting generation. */
const pending = ref<Pending | null>(null)
/** The generated payment-link URL, or null before/while generating. */
const url = ref<string | null>(null)
const submitting = ref(false)
/** i18n key of the current error, or null. */
const error = ref<string | null>(null)

/** Map a `createPaymentLink` failure to an i18n key (draft-creation failures
 *  are mapped separately by `mapDraftError`). */
function mapPaymentLinkError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 404) return 'paymentLink.errors.notFound'
    if (e.status === 400) return 'paymentLink.errors.unavailablePme'
    if (e.status === 422) return 'paymentLink.errors.notEligible'
  }
  return 'paymentLink.errors.generic'
}

export function usePaymentLink() {
  /** Stage the draft id + mode from the recap step, then navigate to the page. */
  function prepare(draftId: string, mode: CheckoutMode): void {
    pending.value = { draftId, mode }
    url.value = null
    error.value = null
  }

  /** True when a draft has been staged and the link hasn't been generated yet. */
  function hasPending(): boolean {
    return pending.value !== null
  }

  /**
   * Generate the shareable link for the staged draft. Consumes the staged context
   * so a later reload can't silently regenerate. No-op (returns false) when
   * nothing is staged or a link is already being/has been generated — the page
   * uses that to redirect home.
   */
  async function generate(): Promise<boolean> {
    if (submitting.value || url.value) return true
    const staged = pending.value
    if (!staged) return false
    pending.value = null

    submitting.value = true
    error.value = null

    // Create (idempotently) the shareable payment link for the recap's draft.
    try {
      const link = await createPaymentLink(staged.draftId, staged.mode)
      url.value = link.url
    } catch (e) {
      error.value = mapPaymentLinkError(e)
    } finally {
      submitting.value = false
    }
    return true
  }

  return { url, submitting, error, prepare, hasPending, generate }
}
